import { useLayoutEffect, useRef, type ReactNode } from "react";
import type { ResolvedLocale } from "../../app/locale.js";
import { StartupScreen } from "./StartupScreen.js";
import "./I18nTransitionBoundary.css";

/** Later language changes hide the existing tree without discarding drafts,
 * one-time tokens or feature state. The same container remains after loading. */
export function I18nTransitionBoundary({ ready, failed, locale, children }: {
  ready: boolean; failed: boolean; locale: ResolvedLocale; children: ReactNode;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (ready || !contentRef.current) return;
    let appContainer: HTMLElement = contentRef.current;
    while (appContainer.parentElement && appContainer.parentElement !== document.body) {
      appContainer = appContainer.parentElement;
    }
    const preserved = new Map<HTMLElement, {
      hidden: string | null; inert: string | null;
      display: string; priority: string; hadStyle: boolean;
    }>();
    const conceal = (element: Element) => {
      if (!(element instanceof HTMLElement) || element === appContainer
        || /^(SCRIPT|STYLE|LINK)$/.test(element.tagName) || preserved.has(element)) return;
      preserved.set(element, {
        hidden: element.getAttribute("hidden"), inert: element.getAttribute("inert"),
        display: element.style.getPropertyValue("display"), priority: element.style.getPropertyPriority("display"),
        hadStyle: element.hasAttribute("style")
      });
      element.setAttribute("hidden", "");
      element.setAttribute("inert", "");
      // Portal classes can override the browser's default [hidden] display rule.
      element.style.setProperty("display", "none", "important");
    };
    for (const element of document.body.children) conceal(element);
    // Feature effects can open a portal after loading begins. Conceal its body
    // root as well; the app container includes the readable recovery screen.
    const observer = new MutationObserver((records) => {
      for (const record of records) for (const node of record.addedNodes) {
        if (node instanceof Element && node.parentElement === document.body) conceal(node);
      }
    });
    observer.observe(document.body, { childList: true });
    return () => {
      observer.disconnect();
      for (const [element, attributes] of preserved) {
        for (const attribute of ["hidden", "inert"] as const) {
          const value = attributes[attribute];
          if (value === null) element.removeAttribute(attribute);
          else element.setAttribute(attribute, value);
        }
        if (attributes.display) element.style.setProperty("display", attributes.display, attributes.priority);
        else element.style.removeProperty("display");
        if (!attributes.hadStyle && !element.style.cssText) element.removeAttribute("style");
      }
    };
  }, [ready]);
  return <>
    <div ref={contentRef} className="i18n-transition-content" hidden={!ready} inert={!ready}>{children}</div>
    {!ready && <StartupScreen locale={locale} failed={failed} onRetry={() => window.location.reload()} />}
  </>;
}
